import { useEffect, useState, useCallback } from "react";
import { Loader2, PlayCircle, Banknote, AlertTriangle, FileWarning, Settings2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DataTable } from "@/components/common/DataTable";
import { TooltipButton } from "@/components/common/Tooltips";
import { api } from "@/lib/api";
import { mxn } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import UltimaCorridaCard from "./UltimaCorridaCard";

// PLD Bloque 1 · Pieza 2 — Efectivo / Operación Relevante / Fraccionamiento (43ª-IV, 14ª, 16ª, 28ª, 30ª-IV).
// Umbrales en USD (convertidos a MXN con el T.C. FIX de Banxico/DOF) y en MXN directos. NUNCA en UMA.
const MONITOR_ROLES = ["admin_entidad", "oficial_cumplimiento", "analista_pld"];
const CONFIG_ROLES = ["admin_entidad", "oficial_cumplimiento"];

function Kpi({ label, value, icon: Icon, tone = "text-primary" }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500"><Icon className={`h-4 w-4 ${tone}`} /> {label}</div>
      <p className="mt-1 font-heading text-2xl font-semibold text-slate-800">{value}</p>
    </div>
  );
}

const CFG_FIELDS = [
  { key: "tipo_cambio_dof", label: "T.C. FIX Banxico/DOF", hint: "actualizar con el publicado" },
  { key: "umbral_relevante_usd", label: "Op. Relevante (USD)", hint: "28ª · 7,500" },
  { key: "umbral_efectivo_pf_mxn", label: "Seguimiento PF (MXN)", hint: "14ª/16ª · 300,000" },
  { key: "umbral_efectivo_pm_mxn", label: "Seguimiento PM (MXN)", hint: "14ª/16ª · 500,000" },
  { key: "umbral_acumulado_mensual_mxn", label: "Acumulado mensual (MXN)", hint: "14ª/16ª · 1,000,000" },
  { key: "umbral_fracc_op_min_usd", label: "Fracc. op. mín. (USD)", hint: "30ª-IV · 500" },
  { key: "umbral_fracc_suma_usd", label: "Fracc. suma (USD)", hint: "30ª-IV · 7,500" },
];

export default function MonitoreoEfectivo() {
  const { user } = useAuth();
  const canMonitor = MONITOR_ROLES.includes(user?.role);
  const canConfig = CONFIG_ROLES.includes(user?.role);
  const [rel, setRel] = useState({ operaciones_relevantes: [], total: 0, total_mxn: 0 });
  const [ef, setEf] = useState({ acumulados: [], alertas: [], umbral_relevante_mxn: 0, config: {} });
  const [cfg, setCfg] = useState({});
  const [busy, setBusy] = useState(false);
  const [savingCfg, setSavingCfg] = useState(false);
  const [corridaRefresh, setCorridaRefresh] = useState(0);

  const load = useCallback(async () => {
    try {
      const [r, e] = await Promise.all([
        api.get("/pld/monitoreo/relevantes").then((x) => x.data),
        api.get("/pld/monitoreo/efectivo").then((x) => x.data),
      ]);
      setRel(r); setEf(e); setCfg(e.config || {});
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const correr = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/pld/monitoreo/perfil/correr");
      toast.success(`Monitoreo: ${data.operaciones_relevantes} relevantes · ${data.alertas_efectivo} seguimiento · ${data.alertas_fraccionamiento} fraccionamiento`,
        { description: `Constancia ${data.constancias?.efectivo?.folio} emitida y sellada.` });
      setCorridaRefresh((v) => v + 1);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo correr el monitoreo"); }
    finally { setBusy(false); }
  };

  const guardarCfg = async () => {
    setSavingCfg(true);
    try {
      const payload = {};
      CFG_FIELDS.forEach((f) => { if (cfg[f.key] !== undefined && cfg[f.key] !== "") payload[f.key] = Number(cfg[f.key]); });
      const { data } = await api.put("/pld/config", payload);
      setCfg(data);
      toast.success("Umbrales y tipo de cambio actualizados");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar la configuración"); }
    finally { setSavingCfg(false); }
  };

  const alertasFracc = (ef.alertas || []).filter((a) => a.regla?.includes("fraccionamiento"));

  return (
    <div className="space-y-5" data-testid="monitoreo-efectivo">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="max-w-2xl text-sm text-slate-500">
          Agrupa el efectivo por cliente/mes, clasifica <strong>Operaciones Relevantes</strong> (28ª, 7,500 USD eq.),
          alerta por <strong>umbrales de seguimiento</strong> (14ª/16ª) y detecta <strong>fraccionamiento</strong> (30ª-IV).
          Umbrales en USD (T.C. DOF) y MXN — nunca en UMA.
        </p>
        {canMonitor && (
          <TooltipButton tooltip="Correr el motor de monitoreo transaccional del periodo"
            data-testid="correr-efectivo-btn" onClick={correr} disabled={busy} className="bg-accent hover:bg-forest-700">
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <PlayCircle className="mr-1.5 h-4 w-4" />} Correr monitoreo
          </TooltipButton>
        )}
      </div>

      {/* sep-2026 · sin T.C. FIX capturado, las reglas en USD (28ª y 30ª-IV) no se pueden
          evaluar: el aviso tiene que verlo cualquier rol, no solo quien parametriza. */}
      {ef.tipo_cambio_pendiente && (
        <div data-testid="aviso-tipo-cambio"
             className="flex items-start gap-2 rounded-md border-2 border-amber-400 bg-amber-50 px-3 py-2.5 text-sm font-medium text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
          <span>
            Captura el tipo de cambio FIX para que el monitoreo funcione.
            <span className="block text-xs font-normal">
              Sin el T.C. FIX publicado en el DOF no se pueden convertir los umbrales en USD:
              las Operaciones Relevantes (28ª) y el fraccionamiento (30ª-IV) no se evalúan.
            </span>
          </span>
        </div>
      )}

      <UltimaCorridaCard modulo="efectivo" refreshKey={corridaRefresh} />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi label="Operaciones relevantes" value={rel.total} icon={Banknote} />
        <Kpi label="Monto relevante (MXN)" value={mxn(rel.total_mxn)} icon={Banknote} tone="text-accent" />
        <Kpi label="Alertas de fraccionamiento" value={alertasFracc.length} icon={FileWarning} tone="text-red-600" />
        <Kpi label="Op. Relevante desde" value={mxn(ef.umbral_relevante_mxn)} icon={Settings2} />
      </div>

      {canConfig && (
        <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4" data-testid="config-efectivo">
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-primary">Parámetros (editables · DOF/Banxico)</p>
          <p className="mb-3 text-[11px] text-amber-600">
            Los umbrales vienen precargados con los valores de referencia de las Disposiciones;
            ajústalos si tu entidad opera con otros. El tipo de cambio FIX lo captura la entidad
            con el publicado en el DOF.
          </p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {CFG_FIELDS.map((f) => (
              <div key={f.key}>
                <Label className="mb-1 block text-xs">{f.label} <span className="text-slate-400">({f.hint})</span></Label>
                <Input data-testid={`cfg-${f.key}`} type="number" step="0.01" value={cfg[f.key] ?? ""}
                  placeholder={f.key === "tipo_cambio_dof" ? "capturar FIX" : ""}
                  className={f.key === "tipo_cambio_dof" && !cfg[f.key] ? "border-amber-400" : ""}
                  onChange={(e) => setCfg((p) => ({ ...p, [f.key]: e.target.value }))} />
              </div>
            ))}
          </div>
          <div className="mt-3 flex justify-end">
            <Button data-testid="guardar-cfg-efectivo-btn" size="sm" variant="outline" onClick={guardarCfg} disabled={savingCfg}>
              {savingCfg ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} Guardar parámetros
            </Button>
          </div>
        </div>
      )}

      <div>
        <h4 className="mb-2 flex items-center gap-2 font-heading text-sm font-semibold text-slate-700"><Banknote className="h-4 w-4 text-accent" /> Operaciones Relevantes (28ª)</h4>
        <DataTable testid="table-relevantes" rows={rel.operaciones_relevantes}
          columns={[
            { key: "fecha", label: "Fecha", kind: "date" },
            { key: "cliente", label: "Cliente" },
            { key: "tipo_cliente", label: "Tipo" },
            { key: "tipo", label: "Modalidad", kind: "badge" },
            { key: "monto_mxn", label: "Monto (MXN)", kind: "currency" },
            { key: "umbral_mxn", label: "Umbral (MXN)", kind: "currency" },
          ]}
          emptyLabel="Sin operaciones relevantes. Corre el monitoreo para clasificar." />
      </div>

      <div>
        <h4 className="mb-2 flex items-center gap-2 font-heading text-sm font-semibold text-slate-700"><AlertTriangle className="h-4 w-4 text-amber-600" /> Acumulado de efectivo por cliente / mes</h4>
        <DataTable testid="table-efectivo-acumulado" rows={ef.acumulados}
          columns={[
            { key: "cliente", label: "Cliente" },
            { key: "periodo", label: "Mes" },
            { key: "efectivo_total", label: "Efectivo acumulado", kind: "currency" },
            { key: "num_operaciones", label: "Operaciones" },
            { key: "max_operacion", label: "Op. mayor", kind: "currency" },
            { key: "supera_acumulado", label: "≥ tope mensual", render: (r) => (r.supera_acumulado ? "Sí" : "—") },
          ]}
          emptyLabel="Sin operaciones en efectivo registradas." />
      </div>

      <div>
        <h4 className="mb-2 flex items-center gap-2 font-heading text-sm font-semibold text-slate-700"><FileWarning className="h-4 w-4 text-red-600" /> Alertas de seguimiento y fraccionamiento (14ª/16ª, 30ª-IV)</h4>
        <DataTable testid="table-alertas-efectivo" rows={ef.alertas}
          columns={[
            { key: "folio", label: "Folio" },
            { key: "cliente", label: "Cliente" },
            { key: "regla", label: "Regla" },
            { key: "nivel_riesgo", label: "Nivel", kind: "risk" },
            { key: "descripcion", label: "Detalle" },
            { key: "estatus", label: "Estatus", kind: "badge" },
          ]}
          emptyLabel="Sin alertas de efectivo/fraccionamiento." />
      </div>
    </div>
  );
}
